/**
 * Admin settings / compliance API client (Plan 03-08, D7 / 5.0k). Consumes the
 * Plan 03-03 consent-version endpoints via the shared admin `apiFetch` (admin JWT
 * + 401→/login) — NOT the candidate surface's candidateFetch.
 *
 * Types mirror backend/app/schemas/consent_version.py EXACTLY (BUG-017):
 *   - GET  /api/admin/compliance/consent-versions       → ConsentVersionListResponse
 *   - POST /api/admin/compliance/consent-versions        → ConsentVersionRow (201)
 */
import { apiFetch } from "@/lib/api";

// ---- Response/request shapes (match backend/app/schemas/consent_version.py) ----

export type ConsentLanguage = "es" | "en" | "de";

// BUG-084 — Stage-1 application consent (Ley 8968) vs Stage-2 video consent (Ley 9877).
export type ConsentDocumentType = "ley_8968_application" | "ley_9877_video";

export interface ConsentVersionRow {
  id: number;
  document_type: ConsentDocumentType;
  version_number: number;
  language_code: ConsentLanguage;
  text_md: string;
  status: "active" | "superseded";
  created_at: string;
  created_by_user_id: number;
}

export interface ConsentVersionListResponse {
  versions: ConsentVersionRow[];
}

export interface ConsentVersionCreate {
  language_code: ConsentLanguage;
  document_type: ConsentDocumentType;
  text_md: string;
}

// ---- Endpoints (Plan 03-03 backend) ----

/** GET — optional `language` filter; omitted = all languages. */
export async function listConsentVersions(
  language?: ConsentLanguage,
): Promise<ConsentVersionListResponse> {
  const qs = language ? `?language=${encodeURIComponent(language)}` : "";
  return apiFetch<ConsentVersionListResponse>(
    `/api/admin/compliance/consent-versions${qs}`,
  );
}

/** POST — create-and-activate; backend returns the new ConsentVersionRow (201). */
export async function createConsentVersion(
  payload: ConsentVersionCreate,
): Promise<ConsentVersionRow> {
  return apiFetch<ConsentVersionRow>(
    `/api/admin/compliance/consent-versions`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

// ---------------------------------------------------------------------------
// Compliance config (Plan 05-08/05-09) — structured Ley-8968 / PRODHAB fields.
// Mirrors backend/app/schemas/compliance.py EXACTLY (BUG-017). Endpoints live at
// /api/compliance/* (NOT /api/admin/compliance/*). content_admin + super_admin (D4).
// ---------------------------------------------------------------------------

export interface ComplianceBarStatusEntry {
  field: string;
  status: "red" | "green";
}

export interface ComplianceConfig {
  id: number;
  company_legal_name: string | null;
  cedula_juridica: string | null;
  address: string | null;
  contact_email: string | null;
  data_protection_contact: string | null;
  consent_text_es: string | null;
  consent_text_en: string | null;
  consent_text_de: string | null;
  mode: "test" | "live";
  privacy_notice_approved: boolean;
  // Phase 5 V1-parity fields
  prodhab_number: string | null;
  contact_phone: string | null;
  data_recipients: string | null;
  video_consent_approved: boolean;
  retention_months: number;
  bar_status: ComplianceBarStatusEntry[];
  can_switch_to_live: boolean;
}

/** PATCH body — every field optional; only sent keys are updated. */
export interface ComplianceConfigUpdate {
  company_legal_name?: string;
  cedula_juridica?: string;
  address?: string;
  contact_email?: string;
  data_protection_contact?: string;
  prodhab_number?: string;
  contact_phone?: string;
  data_recipients?: string;
  privacy_notice_approved?: boolean;
  video_consent_approved?: boolean;
  retention_months?: number;
}

export async function getComplianceConfig(): Promise<ComplianceConfig> {
  return apiFetch<ComplianceConfig>(`/api/compliance/config`);
}

export async function updateComplianceConfig(
  patch: ComplianceConfigUpdate,
): Promise<ComplianceConfig> {
  return apiFetch<ComplianceConfig>(`/api/compliance/config`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

/** POST /mode — throws ApiError(422, {detail:{error:'compliance_incomplete',missing:[]}}) when going live before all-green. */
export async function switchComplianceMode(
  mode: "test" | "live",
): Promise<ComplianceConfig> {
  return apiFetch<ComplianceConfig>(`/api/compliance/mode`, {
    method: "POST",
    body: JSON.stringify({ mode }),
  });
}
